.container {
  position: relative; // For popover positioning
  display: flex;
  flex-direction: column;
  gap: map.get($spacing-scale, 's-0');
  padding: map.get($spacing-scale, 's-0');
  border-radius: map.get($radius, 'md');
  background-color: var(--color-grey-light);
  border: 1px solid var(--color-grey-light);
  width: 100%;
  // Note: overflow: hidden removed to allow popover to display

  @include transition(background-color, box-shadow);

  &:hover {
    box-shadow: 0 1px 4px 0 var(--color-grey-soft);
  }

  // Contacted state - white background with different border
  &.contacted {
    background-color: var(--color-surface-primary);
    border-color: var(--color-grey-soft);
  }
}

.contactInfo {
  display: flex;
  gap: map.get($spacing-scale, 's-3');
  align-items: flex-start;
  padding: map.get($spacing-scale, 's-3'); // 12px - matches WhatsappCard
  width: 100%;
}

.avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: map.get($radius, 'rounded');
  flex-shrink: 0;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.infoWrapper {
  display: flex;
  flex-direction: column;
  gap: map.get($spacing-scale, 's-1'); // 4px - matches WhatsappCard
  min-width: 0;
  flex: 1;
}

.name {
  @include body-medium;
  line-height: 1.2;
  color: var(--color-text-primary);
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.jobTitle {
  @include body-small-regular;
  line-height: 1.2;
  color: var(--color-text-secondary);
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.emailWrapper {
  display: flex;
  align-items: center;
  gap: map.get($spacing-scale, 's-1');
  width: 100%;
}

.email {
  @include metadata-small-regular;
  line-height: 1.2;
  color: var(--color-text-tertiary);
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.verifiedIcon {
  flex-shrink: 0;
}

.copyButton {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  padding: 0;
  background: none;
  border: none;
  border-radius: map.get($radius, 'sm');
  cursor: pointer;
  opacity: 0;

  @include transition(opacity);

  .emailWrapper:hover & {
    opacity: 1;
  }

  &:focus-visible {
    opacity: 1;
    outline: 2px solid var(--color-primary-base);
    outline-offset: 1px;
  }

  &:hover {
    opacity: 1;
  }
}

.actionIcons {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: map.get($spacing-scale, 's-1');
}

.deleteButton {
  flex-shrink: 0;
  opacity: 0;

  .container:hover & {
    opacity: 1;
  }

  @include transition(opacity);
}

// Footer section
.footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: map.get($spacing-scale, 's-3');
  padding: map.get($spacing-scale, 's-3') map.get($spacing-scale, 's-4');
  border-top: 1px solid var(--color-grey-soft);
  width: 100%;
}

// Action buttons (Enrich, Reach Out)
.actionButton {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: map.get($spacing-scale, 's-2');
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;

  @include body-small-medium;
  line-height: 1; // Override mixin - Figma uses 100%
  color: var(--color-text-primary);

  @include transition(opacity);

  &:hover:not(:disabled) {
    opacity: 0.7;
  }

  &:focus-visible {
    outline: 2px solid var(--color-primary-base);
    outline-offset: 2px;
    border-radius: map.get($radius, 'sm');
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

// Reach Out wrapper for sequence popover positioning
.reachOutWrapper {
  position: relative;
}

// Status wrapper for popover positioning
.statusWrapper {
  position: relative;
}

// Status button (In Progress, Completed, Successful)
.statusButton {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: map.get($spacing-scale, 's-2');
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;

  @include body-small-medium;
  line-height: 1; // Override mixin - Figma uses 100%

  @include transition(opacity);

  &:hover {
    opacity: 0.7;
  }

  &:focus-visible {
    outline: 2px solid var(--color-primary-base);
    outline-offset: 2px;
    border-radius: map.get($radius, 'sm');
  }
}

// Sequence Picker Popover (opens above by default, below when not enough space)
.sequencePopover {
  position: absolute;
  bottom: calc(100% + 8px);
  right: 0;
  z-index: 100;
  width: 280px;
  max-width: calc(100vw - 32px);
  background-color: var(--color-surface-glass);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  box-shadow:
    0 2px 4px 0 rgba(0, 0, 0, 0.05),
    0 10px 30px 0 rgba(0, 0, 0, 0.15);
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.sequencePopoverBelow {
  bottom: auto;
  top: calc(100% + 8px);
}

.popoverActions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: map.get($spacing-scale, 's-2');
  padding-top: map.get($spacing-scale, 's-2');
  border-top: 1px solid var(--color-grey-soft);
}

.actionButtonError {
  color: var(--color-semantic-danger);

  &:disabled {
    opacity: 1;
    cursor: default;
  }
}

// Sequence Log Popover (rendered via portal on document.body)
.popover {
  position: fixed;
  z-index: 1000;
  width: 580px;
  max-width: calc(100vw - 32px);
  background-color: var(--color-surface-glass);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  box-shadow:
    0 2px 4px 0 rgba(0, 0, 0, 0.05),
    0 10px 30px 0 rgba(0, 0, 0, 0.15);
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.popoverHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.popoverTitle {
  display: flex;
  align-items: center;
  gap: map.get($spacing-scale, 's-2');

  @include body-small-medium;
  line-height: 16px; // Match icon height for alignment
  color: var(--color-text-secondary);
}

.apolloLink {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  text-decoration: none;
  opacity: 0.7;

  @include transition(opacity);

  &:hover {
    opacity: 1;
  }
}

.closeButton {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  border-radius: map.get($radius, 'sm');

  @include transition(opacity);

  &:hover {
    opacity: 0.7;
  }

  &:focus-visible {
    outline: 2px solid var(--color-primary-base);
    outline-offset: 2px;
  }
}

.popoverTable {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--color-grey-soft);
  border-radius: map.get($radius, 'md');
  width: 100%;
  overflow: hidden;
}

.tableHeader {
  display: grid;
  grid-template-columns: 140px 100px 1fr;
  background-color: var(--color-grey-light);
  border-bottom: 1px solid var(--color-grey-soft);
}

.headerCell {
  padding: map.get($spacing-scale, 's-3');

  @include metadata-medium;
  line-height: 1;
  color: var(--color-text-secondary);
  white-space: nowrap;
}

.tableBody {
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  max-height: 240px;
}

.tableRow {
  display: grid;
  grid-template-columns: 140px 100px 1fr;
  border-bottom: 1px solid var(--color-grey-soft);
  background-color: var(--color-surface-primary);

  &:last-child {
    border-bottom: none;
  }
}

.rowCell {
  padding: map.get($spacing-scale, 's-4') map.get($spacing-scale, 's-3');
  min-width: 0;

  @include caption-regular;
  line-height: 1;
  color: var(--color-text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.subjectCell {
  display: block;
  min-width: 0;
  padding: map.get($spacing-scale, 's-4') map.get($spacing-scale, 's-3');
}

.subjectText {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @include caption-regular;
  line-height: 1;
  color: var(--color-text-primary);
}

// Legacy support - keep old classes for backwards compatibility
.content {
  display: flex;
  flex-direction: column;
  gap: map.get($spacing-scale, 's-1h');
  min-width: 0;
  flex: 1;
}

.metadata {
  @include metadata-small-regular;
  color: var(--color-text-tertiary);
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}
